// R27-3 — MY ROOM'S ONE PANEL, and it comes out because it carries a DECISION nothing has ever run.
//
// Nine call sites: Your Reports, Daily guidance, Your cards, Saved shelf, Bookmarks, Saved Verses,
// Journal, People of Interest, Continue watching. Every block of My Room is one of these, so the
// collapse rule is the same rule nine times and it is written once, here.
//
// THE DECISION, and it is the reason this is worth a file: **A COLLAPSED PANEL DRAWS NO BODY AT ALL.**
// Not hidden with CSS — the children are not rendered. That is load-bearing in both directions and
// neither direction was checked by anything:
//   - The bodies of these panels are the expensive ones (a shelf of covers, a tarot spread, a list of
//     saved verses). A collapsed panel that still mounted its children would pay for every block of
//     My Room on every visit, whatever she had put away.
//   - The children of a collapsed panel are UNMOUNTED, so anything they hold in state is gone when it
//     reopens. That is fine for a list read from the server and would not be fine for a form. Nothing
//     may put a form in here without knowing that, and until now nothing said so.
//
// The toggle's aria-label flips with the state (`Expand …` / `Minimize …`), which is the only thing a
// screen reader has to go on — the visible control is a `+` and a `–`.
//
// `pointer` is the one-time arrival note ("Your charts are ready for you"), passed at exactly one of
// the nine and correctly so: it points at the panel it is about. It renders INSIDE the header, after
// the toggle, so it can be positioned against the panel's own corner.
//
// Props in, tree out, no hook — the entry requirement (R7/R8), so `scripts/r27-featured-tier.mjs`
// CALLS it and reads the tree back instead of matching its source text in a 13,000-line file.

//
// 14-B4: `eyebrow` and `title` arrive as props from nine call sites, so this component translates
// NOTHING it is handed (R24: a component that translated its own text would be overruled by every
// call site anyway). The one sentence it writes ITSELF is the toggle's accessible name, composed from
// the title — and a screen-reader-only name is exactly the kind that stays English forever, because
// nobody looking at the screen can see it.
import type React from "react";
import { t } from "../strings.js";
import { DEFAULT_LANGUAGE } from "../language.js";

export function RoomPanel({
  eyebrow,
  title,
  children,
  wide = false,
  collapsed,
  onToggle,
  pointer = null,
  language = DEFAULT_LANGUAGE,
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
  wide?: boolean;
  collapsed: boolean;
  onToggle: () => void;
  pointer?: React.ReactNode;
  language?: string;
}) {
  return (
    <section className={`room-panel${wide ? " room-panel--wide" : ""}${collapsed ? " is-collapsed" : ""}`}>
      <header className="room-panel__head">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h2>{title}</h2>
        </div>
        <button
          className="room-panel__toggle"
          type="button"
          onClick={onToggle}
          aria-label={t(language, collapsed ? "panel.expand" : "panel.minimize", { title })}
        >
          {collapsed ? "+" : "–"}
        </button>
        {pointer}
      </header>
      {collapsed ? null : <div className="room-panel__body">{children}</div>}
    </section>
  );
}
